import { createContext, useContext, useState, useCallback, useRef, type ReactNode, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface RefreshContextType {
  lastRefresh: Date | null;
  isRefreshing: boolean;
  triggerRefresh: () => Promise<void>;
  dataStatus: {
    isStale: boolean;
    lastUpdate: Date | null;
    minutesSinceUpdate: number;
  } | null;
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

export function RefreshProvider({ children }: { children: ReactNode }) {
  const [lastRefresh, setLastRefresh] = useState<Date | null>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dataStatus, setDataStatus] = useState<RefreshContextType['dataStatus']>({
    isStale: false,
    lastUpdate: new Date(),
    minutesSinceUpdate: 0
  });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Ref para evitar chamadas duplicadas (React StrictMode / race conditions)
  const isRefreshingRef = useRef(false);

  // Update data status every minute
  useEffect(() => {
    const updateDataStatus = () => {
      if (lastRefresh) {
        const minutesPassed = Math.floor((Date.now() - lastRefresh.getTime()) / 60000);
        setDataStatus({
          isStale: minutesPassed > 30,
          lastUpdate: lastRefresh,
          minutesSinceUpdate: minutesPassed
        });
      }
    };

    // Update immediately
    updateDataStatus();

    // Update every minute
    const interval = setInterval(updateDataStatus, 60000);

    return () => clearInterval(interval);
  }, [lastRefresh]);

  const triggerRefresh = useCallback(async () => {
    // Usar ref para evitar race conditions e chamadas duplicadas
    if (isRefreshingRef.current || isRefreshing) {
      console.warn('⏸️ Atualização já em andamento, ignorando chamada duplicada');
      return;
    }

    // Marcar como refreshing imediatamente via ref
    isRefreshingRef.current = true;
    setIsRefreshing(true);

    try {
      console.warn('🔄 Atualizando cache do frontend...');

      toast({
        title: 'Atualizando dados...',
        description: 'Por favor, aguarde.',
        duration: 3000,
      });

      // Verificar se há queries travadas (fetching há muito tempo)
      const queries = queryClient.getQueryCache().getAll();
      const stuckQueries = queries.filter(q =>
        q.state.fetchStatus === 'fetching' &&
        q.state.dataUpdatedAt < Date.now() - 30000 // Mais de 30s em fetching
      );

      if (stuckQueries.length > 0) {
        console.warn(`⚠️ ${stuckQueries.length} queries travadas detectadas, forçando cancelamento`);
      }

      // IMPORTANTE: Primeiro cancelar todas as queries em andamento
      // Isso previne conflitos de cache quando múltiplas requisições estão em voo
      await queryClient.cancelQueries();

      // Aguardar um momento para garantir que as queries foram canceladas
      await new Promise(resolve => setTimeout(resolve, 100));

      // IMPORTANTE: Invalidar e REFETCH ao invés de apenas invalidar
      // refetchType: 'all' garante que todas as queries são recarregadas
      await queryClient.refetchQueries({
        type: 'active', // Apenas queries ativas (montadas)
        stale: true, // Todas que estão stale
      });

      // Atualizar timestamp
      setLastRefresh(new Date());

      toast({
        title: 'Dados atualizados!',
        description: 'Os dados foram atualizados com sucesso.',
        duration: 3000,
      });

    } catch (error) {
      console.error('❌ Erro durante refresh:', error);
      toast({
        title: 'Erro ao atualizar dados',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      // CORRIGIDO: Resetar imediatamente - refetchQueries já aguarda as queries
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, [queryClient, isRefreshing, toast]);

  return (
    <RefreshContext.Provider value={{ lastRefresh, isRefreshing, triggerRefresh, dataStatus }}>
      {children}
    </RefreshContext.Provider>
  );
}

export function useRefresh() {
  const context = useContext(RefreshContext);
  if (!context) {
    throw new Error('useRefresh must be used within RefreshProvider');
  }
  return context;
}